import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cx } from './ui';

export interface CalendarEvent {
  id: string;
  title: string;
  scheduledAt: string;
  status: string;
  type: string;
  location?: string | null;
  cycleNumber?: number | null;
  /** De cuál de sus tratamientos es. Solo importa si tiene más de uno. */
  program?: { id: string; name: string; brand: string | null } | null;
  /**
   * Lo anotó el paciente, no lo programó su equipo.
   *
   * Se dibuja distinto porque son dos cosas: las dosis las genera el programa y
   * son lo que el equipo mira; esto es su agenda. Mezclarlas sin distinguirlas
   * haría creer que el turno que se anotó solo lo sabe su equipo.
   */
  own?: boolean;
}

const WEEKDAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

const MONTHS = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const STATUS_DOT: Record<string, string> = {
  SCHEDULED: 'bg-primary',
  COMPLETED: 'bg-success',
  MISSED: 'bg-danger',
  CANCELLED: 'bg-fg-subtle',
};

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function FiltroChip({
  activo,
  onClick,
  children,
}: {
  activo: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activo}
      className={cx(
        'px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors',
        activo
          ? 'bg-primary text-primary-fg'
          : 'bg-surface text-fg-muted hover:text-fg border border-border',
      )}
    >
      {children}
    </button>
  );
}

/**
 * Calendario mensual con los turnos marcados.
 *
 * Antes esto era una lista de fechas. Un paciente en tratamiento no piensa su
 * mes como una lista: piensa "¿cuándo es la próxima?" y "¿me queda alguna esta
 * semana?", y las dos se contestan de un vistazo mirando una grilla y ninguna
 * leyendo un listado.
 *
 * Se toca un día y se ve qué hay ese día. Los días con algo quedan marcados con
 * un punto del color de su estado, así el mes se lee sin tocar nada.
 */
export function Calendar({
  events,
  programs = [],
  onEventClick,
  onSelect,
}: {
  events: CalendarEvent[];
  /**
   * Sus tratamientos activos. Con dos o más aparece el filtro de arriba.
   *
   * Los puntos de la grilla no pueden llevar el color del tratamiento: ya
   * llevan el del estado, que es lo que distingue una dosis realizada de una
   * que se pasó. Eso no se cambia por una etiqueta. La forma de saber de cuál
   * es cada cosa es mirar un tratamiento por vez, y el nombre en el detalle
   * del día.
   */
  programs?: Array<{ id: string; name: string; brand: string | null }>;
  /** Para abrir lo que el paciente anotó y poder cambiarlo. */
  onEventClick?: (event: CalendarEvent) => void;
  /** Se dispara al elegir un día, con lo que haya ese día. */
  onSelect?: (day: Date, events: CalendarEvent[]) => void;
}) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selected, setSelected] = useState<Date | null>(today);

  /**
   * Qué se está eligiendo: días, meses o años.
   *
   * Con las flechas solas, ir a un turno de hace ocho meses son ocho clics. El
   * mes y el año del encabezado son botones que abren su propia grilla, que es
   * como funciona cualquier calendario que la gente ya usó.
   */
  const [view, setView] = useState<'days' | 'months' | 'years'>('days');

  /** Doce años alrededor del actual: cubre el tratamiento y su seguimiento. */
  const yearStart = cursor.getFullYear() - 6;

  /** `null` es "todos". Con un solo tratamiento el filtro ni se dibuja. */
  const [filtro, setFiltro] = useState<string | null>(null);

  const multiple = programs.length > 1;
  const visibles = filtro ? events.filter((event) => event.program?.id === filtro) : events;

  const byDay = new Map<string, CalendarEvent[]>();
  for (const event of visibles) {
    const key = dayKey(new Date(event.scheduledAt));
    (byDay.get(key) ?? byDay.set(key, []).get(key)!).push(event);
  }

  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();

  // La semana arranca en lunes, como se lee un calendario en castellano.
  const offset = (first.getDay() + 6) % 7;

  const cells: Array<Date | null> = [
    ...Array.from({ length: offset }, () => null),
    ...Array.from(
      { length: daysInMonth },
      (_, index) => new Date(cursor.getFullYear(), cursor.getMonth(), index + 1),
    ),
  ];

  const move = (months: number): void => {
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + months, 1));
  };

  const selectedEvents = selected ? (byDay.get(dayKey(selected)) ?? []) : [];

  return (
    <div>
      {/* Un tratamiento por vez, en lugar de mezclarlos y pedirle al paciente
          que adivine. Se pone arriba del mes y no adentro de la grilla porque
          lo que cambia es qué se está mirando, no cómo se ve. */}
      {multiple && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          <FiltroChip activo={filtro === null} onClick={() => setFiltro(null)}>
            Todos
          </FiltroChip>
          {programs.map((programa) => (
            <FiltroChip
              key={programa.id}
              activo={filtro === programa.id}
              onClick={() => setFiltro(filtro === programa.id ? null : programa.id)}
            >
              {programa.brand ?? programa.name}
            </FiltroChip>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 mb-3">
        <button
          type="button"
          onClick={() => (view === 'years' ? setCursor(new Date(yearStart - 6, cursor.getMonth(), 1)) : move(-1))}
          aria-label={view === 'years' ? 'Años anteriores' : 'Mes anterior'}
          disabled={view === 'months'}
          className="size-9 grid place-items-center rounded-md text-fg-muted hover:bg-surface transition-colors disabled:opacity-30"
        >
          <ChevronLeft className="size-5" />
        </button>

        {/* El mes y el año son botones, no un título. */}
        <p className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setView(view === 'months' ? 'days' : 'months')}
            className={cx(
              'font-semibold capitalize px-2 py-1 rounded-md transition-colors',
              view === 'months' ? 'bg-primary-soft text-primary' : 'hover:bg-surface',
            )}
          >
            {MONTHS[cursor.getMonth()]}
          </button>
          <button
            type="button"
            onClick={() => setView(view === 'years' ? 'days' : 'years')}
            className={cx(
              'font-semibold px-2 py-1 rounded-md transition-colors tabular-nums',
              view === 'years' ? 'bg-primary-soft text-primary' : 'hover:bg-surface',
            )}
          >
            {cursor.getFullYear()}
          </button>
        </p>

        <button
          type="button"
          onClick={() => (view === 'years' ? setCursor(new Date(yearStart + 18, cursor.getMonth(), 1)) : move(1))}
          aria-label={view === 'years' ? 'Años siguientes' : 'Mes siguiente'}
          disabled={view === 'months'}
          className="size-9 grid place-items-center rounded-md text-fg-muted hover:bg-surface transition-colors disabled:opacity-30"
        >
          <ChevronRight className="size-5" />
        </button>
      </div>

      {view === 'months' && (
        <div className="grid grid-cols-3 gap-1.5 animate-fade-up">
          {MONTHS.map((month, index) => (
            <button
              key={month}
              type="button"
              onClick={() => {
                setCursor(new Date(cursor.getFullYear(), index, 1));
                setView('days');
              }}
              className={cx(
                'py-3 rounded-md text-sm capitalize transition-colors',
                index === cursor.getMonth()
                  ? 'bg-primary text-primary-fg font-semibold'
                  : 'hover:bg-surface',
              )}
            >
              {month.slice(0, 3)}
            </button>
          ))}
        </div>
      )}

      {view === 'years' && (
        <div className="grid grid-cols-3 gap-1.5 animate-fade-up">
          {Array.from({ length: 12 }, (_, index) => yearStart + index).map((year) => (
            <button
              key={year}
              type="button"
              onClick={() => {
                setCursor(new Date(year, cursor.getMonth(), 1));
                setView('days');
              }}
              className={cx(
                'py-3 rounded-md text-sm tabular-nums transition-colors',
                year === cursor.getFullYear()
                  ? 'bg-primary text-primary-fg font-semibold'
                  : 'hover:bg-surface',
              )}
            >
              {year}
            </button>
          ))}
        </div>
      )}

      {view === 'days' && (
        <>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((day, index) => (
          <span
            key={index}
            className="text-center text-[11px] font-semibold uppercase text-fg-subtle py-1"
          >
            {day}
          </span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, index) => {
          if (!day) return <span key={`empty-${index}`} />;

          const dayEvents = byDay.get(dayKey(day)) ?? [];
          const isToday = dayKey(day) === dayKey(today);
          const isSelected = selected !== null && dayKey(day) === dayKey(selected);

          return (
            <button
              key={day.getTime()}
              type="button"
              onClick={() => {
                setSelected(day);
                onSelect?.(day, dayEvents);
              }}
              className={cx(
                // `aspect-square` mantiene la grilla proporcionada en cualquier
                // ancho, del teléfono al escritorio, sin medidas fijas.
                'aspect-square rounded-md flex flex-col items-center justify-center gap-1 text-sm',
                'transition-colors relative',
                isSelected
                  ? 'bg-primary text-primary-fg font-semibold'
                  : isToday
                    ? 'bg-primary-soft text-primary font-semibold'
                    : 'hover:bg-surface',
              )}
            >
              {day.getDate()}

              {dayEvents.length > 0 && (
                <span className="flex gap-0.5">
                  {/* Hasta tres puntos: con más, la celda se vuelve ilegible y
                      el detalle está a un toque. */}
                  {dayEvents.slice(0, 3).map((event) => (
                    <span
                      key={event.id}
                      className={cx(
                        'size-1.5 rounded-full',
                        isSelected
                          ? 'bg-primary-fg'
                          : event.own
                            ? 'bg-accent'
                            : (STATUS_DOT[event.status] ?? 'bg-primary'),
                      )}
                    />
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>
        </>
      )}

      {view === 'days' && selected && (
        <div className="mt-4 border-t border-border pt-4">
          <p className="text-sm font-semibold mb-2">
            {selected.getDate()} de {MONTHS[selected.getMonth()]}
          </p>

          {selectedEvents.length === 0 ? (
            <p className="text-sm text-fg-muted">No tenés nada agendado este día.</p>
          ) : (
            <ul className="space-y-2">
              {selectedEvents.map((event) => (
                <li
                  key={event.id}
                  onClick={() => event.own && onEventClick?.(event)}
                  className={cx(
                    'flex items-start gap-3 p-3 rounded-md border border-border bg-surface',
                    event.own && onEventClick && 'cursor-pointer hover:border-accent',
                  )}
                >
                  <span
                    className={cx(
                      'size-2.5 rounded-full shrink-0 mt-1.5',
                      event.own ? 'bg-accent' : (STATUS_DOT[event.status] ?? 'bg-primary'),
                    )}
                  />
                  <div className="min-w-0">
                    {/* Aunque el filtro esté en un programa, el nombre va
                        igual: es el día concreto que el paciente vino a mirar
                        y no tiene por qué acordarse de cómo dejó el filtro. */}
                    {multiple && event.program && (
                      <p className="text-xs font-medium text-primary">
                        {event.program.brand ?? event.program.name}
                      </p>
                    )}
                    <p className="font-medium text-[15px] leading-tight">{event.title}</p>
                    {event.own && <p className="text-xs text-fg-subtle">Lo agregaste vos</p>}
                    <p className="text-sm text-fg-muted mt-0.5">
                      {new Date(event.scheduledAt).toLocaleTimeString('es-AR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      {event.location && ` · ${event.location}`}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
